/* Step 4 — Diligence. Ported from the prototype's diligenceBody(). Four tabs:
   Data room (evidence requests + deck verification), Findings (verified vs open,
   founder questions), Comparables (peer multiples) and Financials (ledger audit).
   Data loads from GET /deal/:id/diligence; actions call the secure data-room link,
   founder-questions and PDF-export endpoints. */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import DealStrip from '../../components/DealStrip';
import TabRow from '../../components/TabRow';
import { Loading, EmptyState } from '../../components/States';
import { useToast } from '../../components/Toast';
import { BASE, levelColor } from '../../helpers';
import { LABELS } from '../../data/config';
import { fetchDiligence, requestDataroom, sendQuestions, exportPdf, ok } from '../../api';

const TABS = [
	{ id: 'dataroom', name: 'Data room' }, { id: 'findings', name: 'Findings' },
	{ id: 'comparables', name: 'Comparables' }, { id: 'financials', name: 'Financials' },
];

const Dot = ({ level }) => <span className="finding__dot" style={{ background: levelColor(level) }} />;

async function openExport(deal, kind, showToast, setBusy) {
	setBusy(true);
	const res = await exportPdf(deal.id, kind);
	setBusy(false);
	if (ok(res) && res.body && res.body.url) { window.open(res.body.url, '_blank', 'noopener'); showToast('Export ready'); }
	else showToast((res && res.message) || 'Export is unavailable right now');
}

function DataRoom({ deal, D, showToast, navigate }) {
	const [busy, setBusy] = useState(false);
	const requests = (D.dataroom && D.dataroom.requests) || [];
	const chase = async () => {
		if (busy) return;
		setBusy(true);
		const pending = requests.filter(([, st]) => st !== 'received').map(([name]) => name);
		const res = await requestDataroom(deal.id, { requests: pending });
		setBusy(false);
		if (ok(res)) showToast('Secure upload link sent to the founder for the pending items');
	};
	return (
		<>
			<div className="grid grid--split45">
				<div className="card">
					<div className="card__label" style={{ marginBottom: 6 }}>Evidence requested from the founder</div>
					{requests.map(([name, st]) => (
						<div key={name} className="reqrow"><span>{name}</span>
							<span className={`chip ${st === 'received' ? 'chip--green' : 'chip--amber'}`} style={{ marginLeft: 'auto' }}>{st === 'received' ? 'Received ✓' : 'Pending'}</span></div>
					))}
					<div style={{ fontSize: 12.5, color: 'var(--dlg-muted)', marginTop: 10 }}>{D.dataroom && D.dataroom.requestsNote}</div>
				</div>
				<div className="card">
					<span className="chip chip--blue" style={{ marginBottom: 10 }}>✦ Verified against the deck as files arrive</span>
					{((D.dataroom && D.dataroom.checks) || []).map((c, i) => <div key={i} className="finding"><Dot level={c.level} /><span>{c.text}</span></div>)}
					<div style={{ fontSize: 12.5, color: 'var(--dlg-muted)', marginTop: 10 }}>{D.dataroom && D.dataroom.checksNote}</div>
				</div>
			</div>
			<div style={{ display: 'flex', gap: 10 }}>
				<button className="pill pill--primary" onClick={() => navigate(`${BASE}/deal/${deal.id}/diligence/findings`)}>Open findings →</button>
				<button className="pill pill--outline" onClick={chase} disabled={busy}>{busy ? 'Sending…' : 'Chase pending items'}</button>
			</div>
		</>
	);
}

function Findings({ deal, D, showToast, navigate }) {
	const [busy, setBusy] = useState(false);
	const findings = D.findings || {};
	const send = async () => {
		if (busy) return;
		setBusy(true);
		const res = await sendQuestions(deal.id, { questions: findings.questions || '' });
		setBusy(false);
		if (ok(res)) showToast('Questions sent to the founder via Diligentsia');
	};
	return (
		<>
			<div className="grid grid--split45">
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>Verified against evidence</div>
					{(findings.verified || []).map((v, i) => (
						<div key={i} className="finding" style={{ alignItems: 'flex-start' }}><Dot level="green" />
							<span><strong style={{ fontWeight: 600 }}>{v.title}</strong> <span className="chip chip--green" style={{ float: 'right' }}>CONFIRMED</span><br />
								<span style={{ color: 'var(--dlg-muted)', fontSize: 12.5 }}>{v.sub}</span></span></div>
					))}
				</div>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>Open issues · carried into the memo</div>
					{(findings.open || []).map((o, i) => (
						<div key={i} className="finding" style={{ alignItems: 'flex-start' }}><Dot level={o.level} />
							<span><strong style={{ fontWeight: 600 }}>{o.title}</strong> <span className={`chip ${o.level === 'red' ? 'chip--red' : 'chip--amber'}`} style={{ float: 'right' }}>{o.tag}</span><br />
								<span style={{ color: 'var(--dlg-muted)', fontSize: 12.5 }}>{o.sub}</span></span></div>
					))}
				</div>
			</div>
			<div className="card">
				<span className="chip chip--blue" style={{ marginBottom: 10 }}>✦ Questions for the founder call — drafted from the findings</span>
				<div style={{ fontSize: 13.5, lineHeight: 1.7, color: 'var(--dlg-ink-soft)' }}>{findings.questions}</div>
			</div>
			<div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
				<button className="pill pill--primary" onClick={() => navigate(`${BASE}/deal/${deal.id}/diligence/comparables`)}>Continue to comparables →</button>
				<button className="pill pill--outline" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Send questions to founder'}</button>
			</div>
		</>
	);
}

function Comparables({ deal, D, showToast, navigate }) {
	const [busy, setBusy] = useState(false);
	const comparables = D.comparables || {};
	return (
		<>
			<div className="card" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
				<div>
					<div className="card__label" style={{ marginBottom: 6 }}>Valuation context</div>
					<div style={{ fontSize: 14.5, color: 'var(--dlg-ink-soft)', maxWidth: 640 }} dangerouslySetInnerHTML={{ __html: comparables.context || '' }} />
				</div>
				{comparables.contextChip ? <span className="chip chip--amber" style={{ marginLeft: 'auto', flex: 'none' }}>● {comparables.contextChip}</span> : null}
			</div>
			<div className="grid grid--split45" style={{ marginTop: 12 }}>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 6 }}>Direct comparables</div>
					{(comparables.peers || []).map((pr, i) => (
						<div key={i} className="peer"><div><div className="peer__name">{pr.name}</div><div className="peer__meta">{pr.meta}</div></div>
							<span className="chip chip--blue" style={{ marginLeft: 'auto' }}>{pr.mult}</span></div>
					))}
					<div style={{ fontSize: 12.5, color: 'var(--dlg-muted)', marginTop: 10 }}>{comparables.peersNote}</div>
				</div>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>Where {deal.name.split(' ')[0]} wins &amp; loses</div>
					{(comparables.winsLoses || []).map((w, i) => <div key={i} className="finding"><Dot level={w.level} /><span>{w.text}</span></div>)}
					<div style={{ fontSize: 12.5, color: 'var(--dlg-muted)', marginTop: 10 }}>Feeds the defensibility test in {LABELS.scenarios}, next step.</div>
				</div>
			</div>
			<div style={{ display: 'flex', gap: 10 }}>
				<button className="pill pill--primary" onClick={() => navigate(`${BASE}/deal/${deal.id}/scenarios`)}>Continue to {LABELS.scenarios} →</button>
				<button className="pill pill--outline" onClick={() => openExport(deal, 'comparables', showToast, setBusy)} disabled={busy}>{busy ? 'Exporting…' : 'Export comparables'}</button>
			</div>
		</>
	);
}

function Financials({ deal, D, showToast, navigate }) {
	const [busy, setBusy] = useState(false);
	const F = D.financials || {};
	return (
		<>
			<div className="card">
				<span className="chip chip--blue" style={{ marginBottom: 14 }}>✦ Financial audit — run on the founder's ledger, not the deck</span>
				<div className="kpis">
					{(F.kpis || []).map(([label, value, sub, tone]) => (
						<div key={label}>
							<div className="kpi__label">{label}</div>
							<div className="kpi__value" style={tone === 'amber' ? { color: 'var(--dlg-amber-ink)' } : undefined}>{value}</div>
							<div className="kpi__sub">{sub}</div>
						</div>
					))}
				</div>
			</div>
			<div className="grid grid--split45" style={{ marginTop: 12 }}>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 10 }}>Revenue quality</div>
					{(F.revenue || []).map((r, i) => (
						<div key={i} style={{ marginBottom: 12 }}>
							<div style={{ fontSize: 13.5, color: 'var(--dlg-ink-soft)', marginBottom: 5 }}>{r.label}</div>
							<div style={{ height: 10, borderRadius: 999, background: 'var(--dlg-fill)' }}><div style={{ width: `${r.pct}%`, height: 10, borderRadius: 999, background: r.color }} /></div>
						</div>
					))}
					{(F.revenueDots || []).map((d, i) => <div key={i} className="finding"><Dot level={d.level} /><span>{d.text}</span></div>)}
				</div>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>What the raise buys</div>
					<div style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--dlg-ink-soft)' }} dangerouslySetInnerHTML={{ __html: F.raise || '' }} />
					{F.raiseDot ? <div className="finding" style={{ marginTop: 8 }}><Dot level={F.raiseDot.level} /><span>{F.raiseDot.text}</span></div> : null}
				</div>
			</div>
			<div style={{ display: 'flex', gap: 10 }}>
				<button className="pill pill--primary" onClick={() => navigate(`${BASE}/deal/${deal.id}/scenarios`)}>Continue to {LABELS.scenarios} →</button>
				<button className="pill pill--outline" onClick={() => openExport(deal, 'audit', showToast, setBusy)} disabled={busy}>{busy ? 'Exporting…' : 'Export audit'}</button>
			</div>
		</>
	);
}

export default function Diligence({ deal }) {
	const { tab: tabParam } = useParams();
	const navigate = useNavigate();
	const showToast = useToast();
	const tab = ['dataroom', 'findings', 'comparables', 'financials'].includes(tabParam) ? tabParam : 'dataroom';
	const [D, setD] = useState(null);
	const [error, setError] = useState(false);

	const load = useCallback(async () => {
		setError(false); setD(null);
		const data = await fetchDiligence(deal.id);
		if (!data) { setError(true); return; }
		setD(data);
	}, [deal.id]);
	useEffect(() => { load(); }, [load]);

	if (error) return (<main className="page screen"><DealStrip deal={deal} stepId="diligence" /><div style={{ marginTop: 14 }}><EmptyState title="No diligence yet" sub="Run the AI analysis first — the diligence workspace is built from it." /></div></main>);
	if (!D) return (<main className="page screen"><DealStrip deal={deal} stepId="diligence" /><div style={{ marginTop: 14 }}><Loading label="Building the diligence workspace with Intelligentsia… this can take up to a minute the first time." /></div></main>);

	const props = { deal, D, showToast, navigate };
	return (
		<main className="page screen">
			<DealStrip deal={deal} stepId="diligence" />
			<TabRow baseHref={`${BASE}/deal/${deal.id}/diligence`} activeTab={tab} tabs={TABS} />
			{tab === 'dataroom' && <DataRoom {...props} />}
			{tab === 'findings' && <Findings {...props} />}
			{tab === 'comparables' && <Comparables {...props} />}
			{tab === 'financials' && <Financials {...props} />}
		</main>
	);
}
